import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import ShareableLinkDocumentsContext from './ShareableLinkDocuments.context';
import { DataRoomNode } from 'router/subrouters/DataRoom/models/DataRoomNode';
import useSelect from 'hooks/useSelect';
import useFileDownload from '../../hooks/useFileDownload';
import { getShareableLinkRoute, sortDataRoomNodes } from 'utils';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import shareableLinksDocuments from '../../api/shareable-links-documents';
import ShareableLinkDataContext from '../ShareableLinkData/ShareableLinkData.context';
import { Params, useLocation, useParams } from 'react-router-dom';
import useQueryParams from 'hooks/useQueryParams';
import { Loader } from 'ncoded-component-library';

const ShareableLinkDocuments: React.FC = (props) => {
  const { children } = props;

  const [isLoading, setIsLoading] = useState(true);
  const [documentRoute, setDocumentRoute] = useState<DataRoomNode[]>([]);
  const [currentlyDisplayedNodes, setCurrentlyDisplayedNodes] = useState<
    DataRoomNode[]
  >([]);

  const {
    shareableLink: { _id: shareableLinkId },
  } = useContext(ShareableLinkDataContext);
  const { popServerError } = useContext(PopNotificationsContext);

  const isEmptyOnThatLevel = useMemo(
    () => !isLoading && !currentlyDisplayedNodes?.length,
    [isLoading, currentlyDisplayedNodes],
  );

  const baseLevelNodes = useMemo(
    () => (!documentRoute?.length ? currentlyDisplayedNodes : []),
    [documentRoute?.length, currentlyDisplayedNodes],
  );

  const { documentId } = useParams<{ documentId: string }>();
  const {
    params: { sort },
  } = useQueryParams<Params>();

  const { pathname } = useLocation();

  const {
    selected,
    isSelectedMode,
    isAllSelected,
    selectAll,
    removeAll,
    updateSelected,
    onSelect,
  } = useSelect<DataRoomNode>(currentlyDisplayedNodes);

  const { download } = useFileDownload(shareableLinkId, baseLevelNodes);

  const baseDocsPath = useMemo(
    () => `${getShareableLinkRoute(shareableLinkId, 'documents')}/`,
    [shareableLinkId],
  );

  const sortCurrentlyDisplayedNodes = useCallback(() => {
    setCurrentlyDisplayedNodes((prev) => sortDataRoomNodes(prev, sort));
  }, [sort, setCurrentlyDisplayedNodes]);

  useEffect(() => {
    (async () => {
      try {
        if (documentId) {
          const { data } = await shareableLinksDocuments.getFolderPath(
            shareableLinkId,
            documentId,
          );

          setDocumentRoute(data.reverse());
        } else {
          setDocumentRoute([]);
        }
      } catch (e) {
        popServerError(e);
      }
    })();
  }, [shareableLinkId, documentId, popServerError]);

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);

        const { data: nodes } =
          await shareableLinksDocuments.getShareableLinkDocuments(
            shareableLinkId,
            documentId,
          );

        setCurrentlyDisplayedNodes(sortDataRoomNodes(nodes, sort));
      } catch (e) {
        popServerError(e);
      } finally {
        setIsLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shareableLinkId, documentId, popServerError, setCurrentlyDisplayedNodes]);

  useEffect(sortCurrentlyDisplayedNodes, [sort, sortCurrentlyDisplayedNodes]);

  useEffect(() => {
    removeAll();
  }, [pathname, removeAll]);

  return (
    <ShareableLinkDocumentsContext.Provider
      value={{
        currentlyDisplayedNodes,
        selected,
        isLoading,
        isSelectedMode,
        isAllSelected,
        documentRoute,
        baseDocsPath,
        isEmptyOnThatLevel,
        selectAll,
        removeAll,
        updateSelected,
        onSelect,
        download,
        setCurrentlyDisplayedNodes,
      }}
    >
      {isLoading && <Loader />}
      {children}
    </ShareableLinkDocumentsContext.Provider>
  );
};

export default ShareableLinkDocuments;
